"use client";

import { useEffect, useRef, useState } from "react";
import { PiCaretDown } from "@/components/icons";
import { useCurrency, type Currency } from "@/contexts/CurrencyContext";

const CURRENCIES: { value: Currency; symbol: string; label: string }[] = [
  { value: "USD", symbol: "$", label: "USD" },
  { value: "EUR", symbol: "€", label: "EUR" },
  { value: "GBP", symbol: "£", label: "GBP" },
];

interface CurrencyDropdownProps {
  className?: string;
  /** When true, trigger shows only the currency symbol (e.g. $). Default shows label + caret. */
  iconOnly?: boolean;
}

export default function CurrencyDropdown({
  className = "",
  iconOnly = false,
}: CurrencyDropdownProps): React.ReactElement {
  const { currency, setCurrency } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const selectedSymbol = CURRENCIES.find((c) => c.value === currency)?.symbol ?? "$";

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        aria-label="Select currency"
        onClick={() => setIsOpen((v) => !v)}
        className="flex items-center gap-[4px] hover:text-accent-gold transition-colors cursor-pointer bg-transparent border-none p-0 font-kode-mono leading-none"
      >
        {iconOnly ? (
          <span className="text-[15px] font-bold">{selectedSymbol}</span>
        ) : (
          <>
            <span className="text-accent-light-title font-bold text-[14px] tracking-[-0.28px]">
              {currency}
            </span>
            <PiCaretDown
              size={11}
              className={`transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"}`}
            />
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-[#1a1a1a] border border-foreground/4 shadow-xl z-50">
          {CURRENCIES.map(({ value, symbol, label }) => (
            <button
              key={value}
              className={`flex items-center gap-2 w-full px-4 py-2 border-none text-[13px] cursor-pointer text-left font-kode-mono hover:bg-white/20 hover:text-accent-gold transition-all duration-300 ${
                value === currency ? "text-accent-gold bg-foreground/5" : "text-foreground bg-transparent"
              }`}
              onClick={() => {
                setCurrency(value);
                setIsOpen(false);
              }}
            >
              <span className="text-[15px] leading-none">{symbol}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
